import type { AccessContext, CatalogOverview, CatalogProductDetail, LeadOverview, LeadView, MarketFilters, MarketOverview, MarketProductDetail, QuantityExamples, SalesCatalogManifest, SalesCatalogSnapshot, SnapshotListing, SalesFiltersState, SalesResponse, SalesStatus, SalesStatusRecord, SessionResponse, SourceDetailResponse, SourceListingDetail, SourceListingsResponse, SourcePeriod, SourcesResponse } from "./types";
import {loadCatalogueDeltas} from './catalogue-delta-loader';

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function jsonRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: { Accept: "application/json", ...(options.body ? { "Content-Type": "application/json" } : {}), ...options.headers },
    cache: "no-store"
  });
  const body = await response.json().catch(() => null) as { error?: string } | T | null;
  if (!response.ok) {
    throw new ApiError(body && typeof body === "object" && "error" in body && body.error ? body.error : "Запрос не выполнен", response.status);
  }
  return body as T;
}

export const getSession = () => jsonRequest<SessionResponse>("/api/auth/session");

export const login = (username: string, password: string) => jsonRequest<AccessContext>("/api/auth/login", {
  method: "POST",
  body: JSON.stringify({ username, password })
});

export const logout = () => jsonRequest<{ ok: true }>("/api/auth/logout", { method: "POST" });

export const saveSalesStatus = (sourceDatabase: string, listingId: string, status: SalesStatus) =>
  jsonRequest<{ sourceDatabase: string; listingId: string; status: SalesStatus }>("/api/sales/status", {
    method: "PUT", body: JSON.stringify({ sourceDatabase, listingId, status })
  });

export const fetchCatalog = (signal?: AbortSignal) =>
  jsonRequest<CatalogOverview>("/api/catalog", { method: "GET", signal });

export const fetchCatalogProduct = (id: string, signal?: AbortSignal) =>
  jsonRequest<CatalogProductDetail>(`/api/catalog/products/${encodeURIComponent(id)}`, { method: "GET", signal });

export function marketQuery(filters: MarketFilters) {
  return new URLSearchParams({ q: filters.q, source: filters.source, coverage: filters.coverage,
    sort: filters.sort, direction: filters.direction, page: String(filters.page), pageSize: String(filters.pageSize) }).toString();
}

export const fetchMarketValue = (filters: MarketFilters, signal?: AbortSignal) =>
  jsonRequest<MarketOverview>(`/api/market-value?${marketQuery(filters)}`, { method: "GET", signal });

export const fetchMarketProduct = (id: string, signal?: AbortSignal) =>
  jsonRequest<MarketProductDetail>(`/api/market-value/products/${encodeURIComponent(id)}`, { method: "GET", signal });

export const fetchLeadChecker = (view: LeadView, q: string, page: number, signal?: AbortSignal) =>
  jsonRequest<LeadOverview>(`/api/lead-checker?${new URLSearchParams({ view, q, page: String(page) })}`, { method: 'GET', signal });
export const fetchQuantityExamples = (signal?: AbortSignal) =>
  jsonRequest<QuantityExamples>('/api/quantity-examples', { method: 'GET', signal });

function sourcePeriodQuery(period: SourcePeriod) {
  const query = new URLSearchParams({ period: period.mode });
  if (period.mode === "range" && period.from && period.to) {
    query.set("from", period.from);
    query.set("to", period.to);
  }
  return query.toString();
}

export const fetchSources = (period: SourcePeriod, signal?: AbortSignal) => jsonRequest<SourcesResponse>(`/api/sources?${sourcePeriodQuery(period)}`, {
  method: "GET",
  signal
});

export const fetchSourceDetail = (id: string, period: SourcePeriod, signal?: AbortSignal) =>
  jsonRequest<SourceDetailResponse>(`/api/sources/${encodeURIComponent(id)}?${sourcePeriodQuery(period)}`, { method: "GET", signal });

export const fetchSourceListings = (id: string, q: string, page: number, signal?: AbortSignal) => {
  const query = new URLSearchParams({ q, page: String(page) });
  return jsonRequest<SourceListingsResponse>(`/api/sources/${encodeURIComponent(id)}/listings?${query}`, { method: "GET", signal });
};

export const fetchSourceListing = (id: string, listingId: string, signal?: AbortSignal) =>
  jsonRequest<SourceListingDetail>(`/api/sources/${encodeURIComponent(id)}/listings/${encodeURIComponent(listingId)}`, { method: "GET", signal });

let cachedSalesSnapshot: { value: SalesCatalogSnapshot; fetchedAt: number } | null = null;

async function loadSalesSnapshot(signal?: AbortSignal): Promise<SalesCatalogSnapshot> {
  const manifest = await jsonRequest<SalesCatalogManifest>('/api/sales/snapshot', { signal });
  if (!Number.isInteger(manifest.chunkCount) || manifest.chunkCount < 0 || manifest.chunkCount > 1000)
    throw new Error('Некорректный снимок каталога');
  const items: SnapshotListing[] = [];
  for (let offset = 0; offset < manifest.chunkCount; offset += 2) {
    const indices = [offset, offset + 1].filter(index => index < manifest.chunkCount);
    const pages = await Promise.all(indices.map(index => jsonRequest<SnapshotListing[]>(
      `/api/sales/snapshot/chunk?version=${manifest.version}&index=${index}`, { signal })));
    for (const page of pages) items.push(...page);
  }
  if (items.length !== manifest.rowCount) throw new Error('Снимок каталога загружен не полностью');
  return loadCatalogueDeltas(manifest,items,path=>jsonRequest(path,{signal}));
}

export async function fetchSalesSnapshot(signal?: AbortSignal, fresh = false) {
  if (!fresh && cachedSalesSnapshot && Date.now() - cachedSalesSnapshot.fetchedAt < 5 * 60_000)
    return cachedSalesSnapshot.value;
  let value: SalesCatalogSnapshot;
  try { value = cachedSalesSnapshot
    ? await loadCatalogueDeltas({...cachedSalesSnapshot.value,chunkCount:0},cachedSalesSnapshot.value.items,
        path=>jsonRequest(path,{signal}))
    : await loadSalesSnapshot(signal); }
  catch (error) {
    if (!(error instanceof ApiError) || error.status !== 409 || signal?.aborted) throw error;
    value = await loadSalesSnapshot(signal);
  }
  cachedSalesSnapshot = { value, fetchedAt: Date.now() };
  return value;
}

export const fetchSalesStatuses = (signal?: AbortSignal) =>
  jsonRequest<{ revision: string | null; items: SalesStatusRecord[] }>('/api/sales/statuses', { signal });

export const fetchSalesStatusVersion = (signal?: AbortSignal) =>
  jsonRequest<{ revision: string | null }>('/api/sales/status-version', { signal });

export async function fetchSales(filters: SalesFiltersState, signal?: AbortSignal): Promise<SalesResponse> {
  const query = new URLSearchParams();
  query.set("period", filters.period);
  if (filters.period === "range") {
    query.set("from", filters.from);
    query.set("to", filters.to);
  }
  if (filters.q.trim()) query.set("q", filters.q.trim());
  if (filters.sources !== null) query.set("sources", filters.sources.join(","));
  if (filters.priceTypes !== null) query.set("priceTypes", filters.priceTypes.join(","));
  if (filters.priceScopes !== null) query.set("priceScopes", filters.priceScopes.join(","));
  if (filters.conditions !== null) query.set("conditions", filters.conditions.join(","));
  if (filters.scoreBands !== null) query.set("scoreBands", filters.scoreBands.join(","));
  query.set("sort", filters.sort);
  query.set("direction", filters.direction);
  query.set("page", String(filters.page));
  query.set("pageSize", String(filters.pageSize));

  return jsonRequest<SalesResponse>(`/api/sales?${query.toString()}`, {
    method: "GET",
    signal
  });
}
